/**
 * Read-only saas-auth identity summary used by the tenant Overview tab.
 *
 * WSRPDM-0045 (tenant overview redesign handoff §3) removed the name + slug from
 * here — they duplicate the heading rendered above the tabs in
 * `TenantDetailPageContent` — and the Email configuration block (three "Not set"
 * lines): configuration, not status, and not yet relocated to Settings (§3 covers
 * only removing it from here; see the delivery's Notes for Architect). The
 * status/signup-mode chips stay — they carry information the page-level heading
 * does not.
 */
import { Alert, Box, Chip, Stack } from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import type { TenantDto } from '@/api/saas/types';

interface TenantIdentityHeaderProps {
  tenant: TenantDto;
  opsNotProvisioned: boolean;
}

export function TenantIdentityHeader({ tenant, opsNotProvisioned }: TenantIdentityHeaderProps) {
  const { t } = useCopy();

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
        <Chip
          size="small"
          label={t(`tenantDetail.identity.status.${tenant.status}`, tenant.status)}
        />
        <Chip
          size="small"
          variant="outlined"
          label={t(`tenants.signupMode.${tenant.signupMode}`, tenant.signupMode)}
        />
      </Stack>

      {opsNotProvisioned ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t(
            'tenantDetail.identity.opsNotProvisioned',
            "Operational settings for this tenant aren't available yet — wsr-api hasn't mirrored it. It will appear on its next roster sync.",
          )}
        </Alert>
      ) : null}
    </Box>
  );
}
